import { useState, useEffect } from "react";
import adminApi from "../services/api";

const Orders = () => {

  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFulfilling, setIsFulfilling] = useState(false);


  // Fetch orders on component mount
  useEffect(() => {
    fetchOrders();
  }, [page]);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching orders...');
      const response = await adminApi.orders.getAll({ page, limit });
      console.log('Orders API response:', response);

      if (response.data) {
        // Handle different possible response structures
        const ordersData = response.data.orders || response.data.data || [];
        const total = response.data.totalPages || Math.ceil((response.data.totalOrders || ordersData.length) / limit) || 1;
        setTotalPages(total);
        console.log('Parsed orders data:', ordersData);

        setOrders(Array.isArray(ordersData) ? ordersData : []);

        if (ordersData.length === 0) {
          console.warn('No orders found in the response');
          console.log('INFO: No orders found');
        } else {
          console.log(`SUCCESS: Loaded ${ordersData.length} orders`);
        }
      } else {
        throw new Error(response.data?.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load orders:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      const errorMessage = error.response?.data?.message ||
        error.message ||
        'Failed to load orders';

      console.log('ERROR:', errorMessage);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleManualFulfill = async (orderId) => {
    if (!window.confirm('Are you sure you want to manually fulfill this order? This will send download links to the customer.')) return;

    setIsFulfilling(true);
    try {
      const response = await adminApi.payments.manualFulfill(orderId);
      if (response.data?.success) {
        console.log('SUCCESS: Order fulfilled successfully!');
        // Update local state
        setOrders(prev => prev.map(o => o.orderId === orderId ? { ...o, status: 'FULFILLED' } : o));
        if (selectedOrder?.orderId === orderId) {
          setSelectedOrder(prev => ({ ...prev, status: 'FULFILLED' }));
        }
      }
    } catch (error) {
      console.error('Manual fulfillment failed:', error);
      console.log('ERROR: Failed to fulfill order');
    } finally {
      setIsFulfilling(false);
    }
  };

  const handleDownloadInvoice = (order) => {
    try {
      // Create invoice content
      const invoiceContent = generateInvoiceHTML(order);

      // Create blob and download
      const blob = new Blob([invoiceContent], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${order.orderId ? order.orderId.slice(-6) : order.id.slice(-6)}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('SUCCESS: Invoice downloaded successfully!');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      console.log('ERROR: Failed to download invoice');
    }
  };

  const generateInvoiceHTML = (order) => {
    const orderDate = formatDate(order.createdAt);
    const totalAmount = Number(order.amount || order.totalAmount || 0).toFixed(2);

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Invoice - ${order.orderId ? order.orderId.slice(-6) : order.id.slice(-6)}</title>
    <style>
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f8f9fa;
            color: #333;
        }
        .invoice-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            overflow: hidden;
        }
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px;
            text-align: center;
        }
        .header h1 {
            margin: 0;
            font-size: 28px;
            font-weight: 600;
        }
        .header p {
            margin: 5px 0 0 0;
            opacity: 0.9;
            font-size: 16px;
        }
        .content {
            padding: 30px;
        }
        .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 30px;
            margin-bottom: 30px;
        }
        .info-section h3 {
            color: #667eea;
            margin: 0 0 15px 0;
            font-size: 18px;
            border-bottom: 2px solid #667eea;
            padding-bottom: 8px;
        }
        .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            padding: 8px 0;
            border-bottom: 1px solid #eee;
        }
        .info-row:last-child {
            border-bottom: none;
        }
        .label {
            font-weight: 600;
            color: #666;
        }
        .value {
            font-weight: 500;
            color: #333;
        }
        .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .items-table th {
            background: #667eea;
            color: white;
            padding: 15px;
            text-align: left;
            font-weight: 600;
        }
        .items-table td {
            padding: 15px;
            border-bottom: 1px solid #eee;
        }
        .items-table tr:hover {
            background-color: #f8f9fa;
        }
        .total-row {
            background: #f8f9fa;
            font-weight: 600;
        }
        .total-row td {
            border-bottom: none;
        }
        .footer {
            background: #f8f9fa;
            padding: 20px 30px;
            text-align: center;
            color: #666;
            border-top: 1px solid #eee;
        }
        .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
        }
        .status-created { background: #e3f2fd; color: #1976d2; }
        .status-pending { background: #fff3e0; color: #f57c00; }
        .status-completed { background: #e8f5e8; color: #388e3c; }
        .status-captured { background: #e8f5e8; color: #388e3c; }
        .status-failed { background: #ffebee; color: #d32f2f; }
        .status-refunded { background: #f3e5f5; color: #7b1fa2; }
        @media print {
            body { background: white; }
            .invoice-container { box-shadow: none; }
        }
    </style>
</head>
<body>
    <div class="invoice-container">
        <div class="header">
            <h1>INVOICE</h1>
            <p>Pixelfable - Professional Presets</p>
        </div>
        
        <div class="content">
            <div class="info-grid">
                <div class="info-section">
                    <h3>Customer Information</h3>
                    <div class="info-row">
                        <span class="label">Name:</span>
                        <span class="value">${order.customer?.name || order.customerName || 'N/A'}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">Email:</span>
                        <span class="value">${order.customer?.email || order.email || 'N/A'}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">Phone:</span>
                        <span class="value">${order.customer?.phone || order.phone || 'N/A'}</span>
                    </div>
                </div>
                
                <div class="info-section">
                    <h3>Order Information</h3>
                    <div class="info-row">
                        <span class="label">Order ID:</span>
                        <span class="value">${order.orderId ? order.orderId : order.id}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">Order Date:</span>
                        <span class="value">${orderDate}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">Status:</span>
                        <span class="value">
                            <span class="status-badge status-${order.status || 'pending'}">${order.status || 'pending'}</span>
                        </span>
                    </div>
                    ${order.paymentId ? `
                    <div class="info-row">
                        <span class="label">Payment ID:</span>
                        <span class="value">${order.paymentId}</span>
                    </div>
                    ` : ''}
                </div>
            </div>
            
            <div class="info-section">
                <h3>Order Items</h3>
                <table class="items-table">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${order.items && order.items.length > 0 ?
        order.items.map(item => `
                                <tr>
                                    <td>${item.name || item.product?.name || 'N/A'}</td>
                                    <td>${item.quantity || 1}</td>
                                    <td>₹${Number(item.price || 0).toFixed(2)}</td>
                                    <td>₹${((item.quantity || 1) * (item.price || 0)).toFixed(2)}</td>
                                </tr>
                            `).join('') :
        '<tr><td colspan="4" style="text-align: center; color: #666;">No items found</td></tr>'
      }
                    </tbody>
                    <tfoot>
                        <tr class="total-row">
                            <td colspan="3" style="text-align: right; font-weight: 600;">Total Amount:</td>
                            <td style="font-size: 18px; color: #667eea;">₹${totalAmount}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
        
        <div class="footer">
            <p>Thank you for your purchase! For support, contact us at pixelfable1825@gmail.com</p>
            <p style="margin-top: 10px; font-size: 12px;">Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
    </div>
</body>
</html>`;
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("images", files[i]);
      }

      const response = await adminApi.orders.uploadImages(selectedOrder.id, formData);
      if (response.data) {
        setUploadedImages(response.data.images);
        setOrders(orders.map(order =>
          order.id === selectedOrder.id ? { ...order, images: response.data.images } : order
        ));
        console.log("SUCCESS: Images uploaded successfully");
      }
    } catch (error) {
      console.error("Failed to upload images:", error);
      console.log("ERROR: Failed to upload images");
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return timeString;
  };

  const getStatusColor = (status) => {
    const s = String(status).toUpperCase();
    switch (s) {
      case "FULFILLED":
      case "COMPLETED":
        return "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20";
      case "CAPTURED":
      case "AUTHORIZED":
        return "bg-blue-500/10 text-blue-400 border border-blue-500/20";
      case "FAILED":
        return "bg-red-500/10 text-red-400 border border-red-500/20";
      case "CREATED":
      case "PENDING":
      case "PROCESSING":
        return "bg-amber-500/10 text-amber-400 border border-amber-500/20";
      case "REFUNDED":
        return "bg-purple-500/10 text-purple-400 border border-purple-500/20";
      default:
        // Support legacy lowercase for existing data
        if (s === "COMPLETED" || s === "CAPTURED") return "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20";
        return "bg-slate-500/10 text-slate-400 border border-slate-500/20";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-900 to-black">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Orders Management</h1>
          <p className="text-gray-400">View and manage customer order details</p>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-black rounded-xl border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-white">
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-800/50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      #{order.orderId ? order.orderId.slice(-6) : order.id.slice(-6)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      <div className="font-medium">{order.customer?.name || order.customerName || 'N/A'}</div>
                      <div className="text-gray-400">{order.customer?.email || order.customerEmail || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-white">
                      {order.items?.length || 0} items
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      ₹{order.amount || order.totalAmount || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status || 'pending')}`}>
                        {order.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => handleViewDetails(order)}
                        className="text-cyan-400 hover:text-cyan-300 transition-colors duration-200 bg-cyan-500/10 px-3 py-1 rounded-lg hover:bg-cyan-500/20"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className={`px-4 py-2 rounded-lg border ${page === 1 ? 'opacity-50 cursor-not-allowed border-gray-800 text-gray-500' : 'border-gray-700 text-white hover:bg-gray-800'}`}
        >
          Previous
        </button>
        <span className="text-gray-400 text-sm">Page {page} of {totalPages}</span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          className={`px-4 py-2 rounded-lg border ${page >= totalPages ? 'opacity-50 cursor-not-allowed border-gray-800 text-gray-500' : 'border-gray-700 text-white hover:bg-gray-800'}`}
        >
          Next
        </button>
      </div>

      {/* Order Details Modal */}
      {isModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700/50 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">

            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-gray-800 to-gray-900 rounded-t-2xl p-6 border-b border-gray-700/50">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Order #{selectedOrder.orderId ? selectedOrder.orderId.slice(-6) : selectedOrder.id.slice(-6)}</h2>
                    <p className="text-gray-400 text-sm">Order Details & Information</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200 hover:scale-105"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">

              {/* Customer & Order Info Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Customer Information */}
                <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white">Customer Information</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/30">
                      <span className="text-gray-400 text-sm">Name:</span>
                      <span className="text-white font-medium">{selectedOrder.customer?.name || selectedOrder.customerName || "N/A"}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/30">
                      <span className="text-gray-400 text-sm">Email:</span>
                      <span className="text-white font-medium">{selectedOrder.customer?.email || selectedOrder.customerEmail || "N/A"}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-400 text-sm">Phone:</span>
                      <span className="text-white font-medium">{selectedOrder.customer?.phone || selectedOrder.phone || "N/A"}</span>
                    </div>
                  </div>
                </div>

                {/* Order Information */}
                <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white">Order Information</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/30">
                      <span className="text-gray-400 text-sm">Order ID:</span>
                      <span className="text-white font-mono text-xs bg-gray-700 px-2 py-1 rounded">{selectedOrder.orderId || selectedOrder.id}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/30">
                      <span className="text-gray-400 text-sm">Order Date:</span>
                      <span className="text-white font-medium">{formatDate(selectedOrder.createdAt)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/30">
                      <span className="text-gray-400 text-sm">Status:</span>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedOrder.status || 'pending')}`}>
                        {selectedOrder.status || 'pending'}
                      </span>
                    </div>
                    {selectedOrder.paymentId && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-400 text-sm">Payment ID:</span>
                        <span className="text-white font-mono text-xs bg-gray-700 px-2 py-1 rounded">{selectedOrder.paymentId}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-gray-800/50 rounded-xl border border-gray-700/30 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-5 py-4 border-b border-gray-700/30">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white">Order Items</h3>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-900/80">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Item</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Qty</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Price</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {selectedOrder.items && selectedOrder.items.length > 0 ? (
                        selectedOrder.items.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-700/30 transition-colors duration-200">
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-white font-medium">
                              {item.name || item.product?.name || `Item ${index + 1}`}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-300">
                              {item.quantity || 1}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-300">
                              ₹{Number(item.price || 0).toFixed(2)}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-white font-semibold">
                              ₹{((item.quantity || 1) * (item.price || 0)).toFixed(2)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-5 py-8 text-center text-sm text-gray-400">
                            <div className="flex flex-col items-center gap-2">
                              <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.172 13H4m16 0h-2" />
                              </svg>
                              <span>No items found in this order</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="bg-gradient-to-r from-gray-900 to-gray-800">
                      <tr>
                        <td colSpan="3" className="px-5 py-4 text-right text-lg font-bold text-white">Total Amount:</td>
                        <td className="px-5 py-4 text-lg font-bold text-green-400">
                          ₹{Number(selectedOrder.amount || selectedOrder.totalAmount || 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Gateway History (Upgrade 8) */}
              {selectedOrder.rawGatewayResponse && (
                <div className="bg-gray-800/50 rounded-xl border border-gray-700/30 overflow-hidden">
                  <div className="bg-gray-800 px-5 py-4 border-b border-gray-700/30 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Gateway Log History
                    </h3>
                  </div>
                  <div className="p-4 max-h-48 overflow-y-auto">
                    <pre className="text-xs text-gray-400 bg-black/30 p-3 rounded-lg font-mono">
                      {JSON.stringify(typeof selectedOrder.rawGatewayResponse === 'string' ? JSON.parse(selectedOrder.rawGatewayResponse) : selectedOrder.rawGatewayResponse, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-700/30">
                {/* Manual Fulfill Button */}
                {['CAPTURED', 'AUTHORIZED', 'captured'].includes(selectedOrder.status) && (
                  <button
                    onClick={() => handleManualFulfill(selectedOrder.orderId)}
                    disabled={isFulfilling}
                    className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium transition-all duration-200 hover:scale-105 flex items-center gap-2 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {isFulfilling ? 'Processing...' : 'Force Manual Fulfillment'}
                  </button>
                )}
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-all duration-200 hover:scale-105"
                >
                  Close
                </button>
                <button
                  onClick={() => handleDownloadInvoice(selectedOrder)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-medium transition-all duration-200 hover:scale-105 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
